import { getLives, getPosts } from '@/api/fbapi';
import { getListAutoReplies, getOutOfStockToday, getQueueCustomers } from '@/api/sale-api';
import { toast } from '@/hooks/use-toast';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { AutoReply, FBLive, FBPost, QueueCustomer } from '@/types/fb-type';
import { ProductVariant } from '@/types/product-type';
import { LiveUpdateWsEvent, OutOfStockWsEvent, WsEvent } from '@/types/ws-type';
import { useCallback, useEffect, useState } from 'react';
import type { SaleInvalidationOptions } from './use-sale-invalidation';
import {
  mergeLivesWithRemembered,
  rememberActiveLive,
  upsertLiveIntoList,
} from '../lib/live-active-store';
import { isCurrentlyLiveStatus, sortLivesForSidebar, withEffectiveStatus } from '../lib/live-status';

type UseSaleSidebarDataOptions = {
  scheduleInvalidation?: (options?: SaleInvalidationOptions) => void;
};

export function useSaleSidebarData(options: UseSaleSidebarDataOptions = {}) {
  const { scheduleInvalidation } = options;
  const [posts, setPosts] = useState<FBPost[]>([]);
  const [isLoadingPost, setIsLoadingPost] = useState(false);
  const [fbLives, setFbLives] = useState<FBLive[]>([]);
  const [loadingFbLives, setLoadingFbLives] = useState(false);
  const [qCustomers, setQCustomers] = useState<QueueCustomer[]>([]);
  const [autoReplies, setAutoReplies] = useState<AutoReply[]>([]);
  const [outOfStocks, setOutOfStocks] = useState<ProductVariant[]>([]);

  const fetchQueueCustomers = useCallback(async () => {
    const resp = await getQueueCustomers();
    if (resp.success) {
      setQCustomers(resp.data);
    }
  }, []);

  const fetchLiveVideos = useCallback(async () => {
    setLoadingFbLives(true);
    const res = await getLives();
    if (res.success) {
      setFbLives(mergeLivesWithRemembered(res.data ?? []));
    } else {
      toast({
        title: 'Error',
        description: res.message || 'Error getting Facebook live for sale',
        variant: 'destructive',
      });
    }
    setLoadingFbLives(false);
  }, []);

  const upsertActiveLive = useCallback((input: { postId: string; title?: string }) => {
    setFbLives((prev) => upsertLiveIntoList(prev, input));
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setIsLoadingPost(true);
        const res = await getPosts();
        if (res.success) setPosts(res.data);
      } catch {
        toast({ title: 'Error', description: 'Error fetching posts', variant: 'destructive' });
      } finally {
        setIsLoadingPost(false);
      }
    };

    const fetchAutoReplies = async () => {
      const resp = await getListAutoReplies();
      if (resp.success) setAutoReplies(resp.data);
    };

    const fetchOutOfStockForToday = async () => {
      const resp = await getOutOfStockToday();
      if (resp.success) setOutOfStocks(resp.data);
    };

    fetchPosts();
    fetchLiveVideos();
    fetchAutoReplies();
    fetchQueueCustomers();
    fetchOutOfStockForToday();
  }, [fetchLiveVideos, fetchQueueCustomers]);

  const handleOutOfStockWsMessage = useCallback(
    async (stompMsg: { body: string }) => {
      try {
        const event: WsEvent = JSON.parse(stompMsg.body);
        if (event.type === 'OUT_OF_STOCK') {
          const outOfStockEvent = event as OutOfStockWsEvent;
          toast({
            title: 'Out of stock',
            description: `Product ${outOfStockEvent.payload.sku} is out of stock`,
            variant: 'destructive',
          });
          setOutOfStocks((prev) => {
            if (prev.some((item) => item.id === outOfStockEvent.payload.id)) return prev;
            return [...prev, outOfStockEvent.payload];
          });
          scheduleInvalidation?.({ pins: true });
        }
      } catch {
        // ignore malformed WS payloads
      }
    },
    [scheduleInvalidation],
  );

  useWebSocketChannel('/topic/out-of-stock', handleOutOfStockWsMessage);

  const handleLiveUpdate = useCallback((stompMsg: { body: string }) => {
    try {
      const event: WsEvent = JSON.parse(stompMsg.body);
      if (event.type === 'LIVE_VIDEO_UPDATED') {
        const liveUpdateEvent = event as LiveUpdateWsEvent;
        const raw = liveUpdateEvent.payload;
        if (isCurrentlyLiveStatus(raw.status)) {
          rememberActiveLive(raw);
        }
        const payload = withEffectiveStatus(raw);
        setFbLives((prev) => {
          const existingIndex = prev.findIndex((live) => live.id === payload.id);
          if (existingIndex !== -1) {
            const updated = [...prev];
            updated[existingIndex] = payload;
            return sortLivesForSidebar(updated);
          }
          return sortLivesForSidebar([payload, ...prev]);
        });
      }
    } catch {
      // ignore malformed WS payloads
    }
  }, []);

  useWebSocketChannel('/topic/live-video', handleLiveUpdate);

  const handleQueueMessage = useCallback(() => {
    void fetchQueueCustomers();
  }, [fetchQueueCustomers]);

  useWebSocketChannel('/topic/q-customers', handleQueueMessage);
  useWebSocketChannel('/topic/clear-q', handleQueueMessage);

  return {
    posts,
    isLoadingPost,
    fbLives,
    loadingFbLives,
    qCustomers,
    autoReplies,
    outOfStocks,
    setOutOfStocks,
    fetchLiveVideos,
    upsertActiveLive,
    fetchQueueCustomers,
  };
}
